<template>
  <div class="multiple-file-loader">
    <div class="multiple-file-loader__table-wrapper">
      <div v-if="filesList.length === 0" class="multiple-file-loader__no-files">
        <v-icon size="50" color="#c6c6c6"
          >mdi-file-document-remove-outline</v-icon
        >
        <div class="multiple-file-loader__no-files-title">
          {{ $t('auto.filesListEmpty') }}
        </div>
      </div>

      <table v-else class="multiple-file-loader__table">
        <tbody>
          <tr
            v-for="(file, idx) in filesList"
            :key="file.id + file.name"
            :class="{ 'new-file': file.new }"
          >
            <td>
              <v-icon v-if="file.new" color="green" size="25"
                >mdi-new-box</v-icon
              >
              <v-checkbox
                v-else-if="!noDownload"
                v-model="checkedFiles"
                color="warning"
                :value="file"
              />
            </td>
            <td style="min-width:20px">
              <b>{{ idx + 1 }})</b>
            </td>
            <td>
              <div class="multiple-file-loader__file-name">
                {{ file.name }}
              </div>
            </td>
            <td>
              <v-btn
                v-if="file.new"
                color="warning"
                icon
                @click="onNewFileDelete(idx)"
              >
                <v-icon>mdi-window-close</v-icon>
              </v-btn>
              <v-menu v-else-if="!noDelete">
                <template #activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    color="red"
                    icon
                    :loading="deletingFiles.includes(file.id)"
                  >
                    <v-icon>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </template>

                <div class="multiple-file-loader__delete-confirm">
                  <b>{{ $t('common.btns.delete') }} ?</b>

                  <div class="d-flex">
                    <v-btn
                      class="mr-3"
                      color="warning"
                      elevation="0"
                      small
                      @click="$emit('delete', file.id)"
                      >{{ $t('common.btns.ok') }}</v-btn
                    >
                    <v-btn color="grey" elevation="0" dark small>{{
                      $t('common.btns.cancel')
                    }}</v-btn>
                  </div>
                </div>
              </v-menu>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="d-flex align-center justify-space-between mt-4">
      <v-btn
        v-if="!noDownload"
        color="blue"
        outlined
        :disabled="!checkedFiles.length"
        :loading="downloading"
        @click="onDownload"
      >
        <v-icon left>mdi-download</v-icon>
        {{ $t('common.btns.downloadChecked') }}
      </v-btn>

      <div v-if="!readOnly" class="multiple-file-loader__add">
        <v-btn color="green" outlined @click="onAddClick">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('common.btns.addFiles') }}
        </v-btn>
        <input ref="fileInput" type="file" @change="onFilesChange" />
      </div>
    </div>

    <v-btn
      v-if="!readOnly"
      class="mt-4"
      elevation="0"
      color="warning"
      block
      :disabled="saveDisabled"
      :loading="uploading"
      @click="onSave"
    >
      {{ $t('common.btns.save') }}
    </v-btn>
  </div>
</template>

<script>
export default {
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    files: {
      type: Array,
      default: () => [],
    },
    uploading: {
      type: Boolean,
      default: false,
    },
    downloading: {
      type: Boolean,
      default: false,
    },
    deletingFiles: {
      type: Array,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    noDownload: {
      type: Boolean,
      default: false,
    },
    noDelete: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    checkedFiles: [],
    filesList: [],
  }),
  watch: {
    files: {
      deep: true,
      immediate: true,
      handler(value) {
        this.filesList = [...value];
      },
    },
    // uploading(value) {
    //   if (!value) this.filesList = [];
    // },
  },
  computed: {
    saveDisabled() {
      return this.filesList.every((file) => !file.new);
    },
  },
  methods: {
    onAddClick() {
      this.$refs.fileInput.click();
    },
    onFilesChange(event) {
      const { files } = event.srcElement;
      const lastFile = files[files.length - 1];
      const lastSavedFile = this.filesList[this.filesList.length - 1];

      if (lastFile === lastSavedFile || !lastFile) return;

      lastFile.new = true;
      this.filesList.push(lastFile);
    },
    onNewFileDelete(idx) {
      this.filesList = this.filesList.filter((_, index) => index !== idx);
    },
    onDownload() {
      this.$emit('download', this.checkedFiles);
      this.checkedFiles = [];
    },
    onSave() {
      const newFiles = this.filesList.filter((item) => item.new);
      this.$emit('save', newFiles);
    },
  },
};
</script>

<style lang="scss">
.multiple-file-loader {
  &__table {
    border-collapse: collapse;
    width: 100%;

    &-wrapper {
      overflow-y: auto;
      max-height: 360px;
      border-radius: 5px;

      &::-webkit-scrollbar {
        width: 10px;
        background-color: transparent;
      }
      &::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.15);
        background-clip: padding-box;
        border-radius: 20px;
        border: solid 2px rgba(0, 0, 0, 0);
      }
      &::-webkit-scrollbar-button {
        width: 0;
        height: 0;
        display: none;
      }
      &::-webkit-scrollbar-corner {
        background-color: transparent;
      }
    }

    tr {
      background-color: #eee;

      &:not(:first-child) td {
        border-top: 2px solid #fff;
      }

      &.new-file {
        background-color: #fee89f;

        &:not(:first-child) td {
          border-top: 2px dashed #fff;
        }
      }
    }

    td {
      font-size: 14px;
      color: #464646;
      height: 68px;

      &:first-child {
        padding-left: 15px;
      }

      &:last-child {
        padding-right: 5px;
      }
    }
  }

  &__add {
    input[type='file'] {
      display: none;
    }
  }

  &__file-name {
    width: 240px;
    word-break: break-all;
  }

  &__no-files {
    height: 100px;
    padding: 10px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: #c6c6c6;

    &-title {
      font-size: 18px;
    }
  }

  &__delete-confirm {
    padding: 10px;
    background-color: #fff;

    b {
      display: block;
      margin-bottom: 5px;
    }
  }
}
</style>
